/**
 * Batch List Component - Display and manage batches
 */
import React, { useState } from 'react';
import { 
  FolderOpen, ChevronDown, ChevronRight, ExternalLink, 
  QrCode, FileDown, Trash2, BarChart3 
} from 'lucide-react';
import { toast } from 'sonner';

export const BatchList = ({ 
  batches = [], 
  batchStats = {}, 
  tags = [],
  onDownloadQR,
  onDeleteBatch,
  onViewAnalytics 
}) => {
  const [expandedFolders, setExpandedFolders] = useState({});

  const toggleFolder = (batch) => {
    setExpandedFolders(prev => ({ ...prev, [batch]: !prev[batch] }));
  };

  const exportBatchCSV = (batchName, batchTags) => {
    const baseUrl = 'https://app.scanit2go.com';
    const csvContent = [
      'NFC Code,Upload URL,View URL,Has Content,Batch',
      ...batchTags.map(t => 
        `${t.nfc_code},${baseUrl}/${t.nfc_code}/tag,${baseUrl}/${t.nfc_code},${t.has_content || false},${batchName}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${batchName}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success(`Downloaded ${batchName}.csv`);
  };

  if (Object.keys(batchStats).length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No batches found</p>
        <p className="text-sm">Generate UUIDs to create your first batch</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(batchStats).map(([batch, data]) => {
        const batchTags = tags.filter(t => t.batch === batch);
        const isExpanded = expandedFolders[batch];
        
        return (
          <div 
            key={batch} 
            className="rounded-2xl border border-border/40 bg-card/50 overflow-hidden"
            data-testid={`batch-${batch}`}
          >
            {/* Batch Header */}
            <div className="p-4 flex items-center justify-between">
              <button
                onClick={() => toggleFolder(batch)}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-1"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
                <FolderOpen className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <span className="font-semibold">{batch}</span>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{data.tag_count} tags</span>
                    <span>•</span>
                    <span>{data.tags_with_content} with content</span>
                    <span>•</span>
                    <span>{data.total_views || 0} views</span>
                  </div>
                </div>
              </button>
              
              {/* Batch Actions */}
              <div className="flex items-center gap-2">
                {data.created_at && (
                  <span className="text-xs text-muted-foreground hidden md:block">
                    Created: {new Date(data.oldest_date || data.created_at).toLocaleDateString()}
                  </span>
                )}
                <button
                  onClick={() => exportBatchCSV(batch, batchTags)}
                  className="px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 transition-colors flex items-center gap-2 text-sm"
                  title="Export to CSV"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button
                  onClick={() => onDeleteBatch(batch)}
                  className="px-4 py-2 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors flex items-center gap-2 text-sm"
                  title="Delete Batch"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            </div>

            {/* Expanded Tag List */}
            {isExpanded && (
              <div className="border-t border-border/40 bg-secondary/10 p-4">
                {batchTags.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No tags in this batch</p>
                ) : (
                  <div className="grid gap-2 max-h-96 overflow-y-auto">
                    {batchTags.map(tag => (
                      <div 
                        key={tag.nfc_code} 
                        className="flex items-center justify-between bg-background/50 rounded-xl p-3"
                      >
                        <div className="flex-1 min-w-0">
                          <code className="text-sm font-mono truncate block">{tag.nfc_code}</code>
                          {tag.description && (
                            <p className="text-xs text-muted-foreground truncate">{tag.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            tag.has_content || tag.message || (tag.media_files?.length > 0)
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tag.has_content || tag.message || (tag.media_files?.length > 0) ? 'Has Content' : 'Empty'}
                          </span>
                          <button 
                            onClick={() => window.open(`/${tag.nfc_code}`, '_blank')} 
                            className="p-2 hover:bg-secondary rounded-full"
                            title="View Tag"
                          >
                            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button 
                            onClick={() => onDownloadQR(tag.nfc_code)} 
                            className="p-2 hover:bg-secondary rounded-full"
                            title="Download QR"
                          >
                            <QrCode className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          {onViewAnalytics && (
                            <button 
                              onClick={() => onViewAnalytics(tag.nfc_code)} 
                              className="p-2 hover:bg-secondary rounded-full"
                              title="View Analytics"
                            >
                              <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BatchList;
